import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
} from "@angular/core";

export interface Message {
  id: string;
  text: string;
  time: string;
  mine: boolean;
  replyTo?: { id: string; text: string };
  imageUrl?: string;
  viewOnce?: boolean;
}

@Component({
  selector: "app-message-bubble",
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./message-bubble.component.html",
  styleUrl: "./message-bubble.component.scss",
})
export class MessageBubbleComponent {
  readonly message = input.required<Message>();
  readonly isMenuOpen = input<boolean>(false);

  readonly toggleMenu = output<string>();
  readonly setReply = output<Message>();
  readonly unsend = output<string>();
  readonly scrollToReply = output<string>();
  readonly viewImage = output<string>();

  private readonly destroyRef = inject(DestroyRef);

  readonly reactionPickerOpen = signal(false);
  readonly viewedLocal = signal(false);
  readonly lightboxOpen = signal(false);
  private viewTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.viewTimer) clearTimeout(this.viewTimer);
    });
  }
  readonly reactions = signal<string[]>([]);
  readonly pickerBottom = signal(0);
  readonly pickerLeft = signal(0);

  // Mobile touch state
  readonly swipeOffset = signal(0);
  readonly isSwiping = signal(false);
  readonly swipeIndicatorOpacity = computed(() =>
    Math.min(Math.abs(this.swipeOffset()) / this.SWIPE_THRESHOLD, 1),
  );

  private touchStartX = 0;
  private touchStartY = 0;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressTriggered = false;
  private skipNextClick = false;
  private readonly LONG_PRESS_MS = 500;
  private readonly SWIPE_THRESHOLD = 60;

  readonly quickEmojis = ["❤️", "😂", "😍", "😢", "😡", "👍", "🙏", "😮", "😅"];

  onBubbleClick(event: MouseEvent) {
    event.stopPropagation();
    if (this.skipNextClick) {
      this.skipNextClick = false;
      return;
    }
    this.closeReactionPicker();
    this.toggleMenu.emit(this.message().id);
  }

  onTouchStart(event: TouchEvent) {
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.longPressTriggered = false;

    this.longPressTimer = setTimeout(() => {
      this.longPressTriggered = true;
      this.skipNextClick = true;
      if (navigator.vibrate) navigator.vibrate(30);
      this.closeReactionPicker();
      this.toggleMenu.emit(this.message().id);
    }, this.LONG_PRESS_MS);
  }

  onTouchMove(event: TouchEvent) {
    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    // Cancel long press on any significant movement
    if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    }

    // Detect horizontal swipe (more horizontal than vertical)
    if (Math.abs(deltaX) > Math.abs(deltaY) * 1.2 && Math.abs(deltaX) > 8) {
      const mine = this.message().mine;
      // Received: swipe right; Mine: swipe left
      const validSwipe = mine ? deltaX < 0 : deltaX > 0;
      if (validSwipe) {
        const clamped = Math.min(Math.abs(deltaX), this.SWIPE_THRESHOLD + 20);
        this.swipeOffset.set(mine ? -clamped : clamped);
        this.isSwiping.set(true);
      }
    }
  }

  onTouchEnd(_event: TouchEvent) {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    if (
      Math.abs(this.swipeOffset()) >= this.SWIPE_THRESHOLD &&
      !this.longPressTriggered
    ) {
      this.skipNextClick = true;
      if (navigator.vibrate) navigator.vibrate(20);
      this.setReply.emit(this.message());
    }

    this.swipeOffset.set(0);
    this.isSwiping.set(false);
  }

  toggleReactionPicker(event: MouseEvent) {
    event.stopPropagation();
    if (this.reactionPickerOpen()) {
      this.reactionPickerOpen.set(false);
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const pickerW = 280;
    const rawLeft = rect.left + rect.width / 2 - pickerW / 2;
    this.pickerBottom.set(window.innerHeight - rect.top + 8);
    this.pickerLeft.set(
      Math.max(8, Math.min(rawLeft, window.innerWidth - pickerW - 8)),
    );
    this.reactionPickerOpen.set(true);
  }

  toggleReaction(emoji: string, event: MouseEvent) {
    event.stopPropagation();
    this.reactions.update((r) =>
      r.includes(emoji) ? r.filter((e) => e !== emoji) : [...r, emoji],
    );
    this.reactionPickerOpen.set(false);
  }

  closeReactionPicker() {
    this.reactionPickerOpen.set(false);
  }

  onViewOnce() {
    if (this.viewedLocal()) return;
    this.viewedLocal.set(true);
    this.lightboxOpen.set(true);
    this.viewTimer = setTimeout(() => {
      this.lightboxOpen.set(false);
      this.viewImage.emit(this.message().id);
    }, 3000);
  }

  closeLightbox() {
    if (this.viewTimer) {
      clearTimeout(this.viewTimer);
      this.viewTimer = undefined;
    }
    this.lightboxOpen.set(false);
    this.viewImage.emit(this.message().id);
  }
}
